import { default as React, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import Alert from '../../core/components/Alert.tsx';
import BooleanField from '../../core/components/Fields/BooleanField.tsx';
import StringField from '../../core/components/Fields/StringField.tsx';
import TagsField from '../../core/components/Fields/TagsField.tsx';
import FormField from '../../core/components/FormField.tsx';
import SaveStatus from '../../core/components/SaveStatus.tsx';
import SettingsFormCard from '../../core/components/SettingsFormCard.tsx';
import { useToaster } from '../../core/components/Toast.tsx';
import countFieldErrors from '../../core/utils/countFieldErrors.ts';
import useOutputMapperConfigMissing from '../../customer/components/OutputMapper/useOutputMapperConfigMissing.ts';

const FORM_DEFAULT_VALUES = {
    useOutputMapping: false,
    enabled: true,
    serializerModule: '',
    headerFields: [],
    lineItemFields: [],
    exportExternalDocumentLink: true,
    exportReceivedBy: false,
    exportWorkistUrl: false,
    useShortExternalFileName: false,
};

const XmlSerializerConfig = ({
    config,
    onActivate,
    onSubmit,
    supportsOutputMapper = false,
    useOutputMapping = false,
    outputMapperKey = '',
    channelId = undefined,
}) => {
    const { t } = useTranslation('config');
    const { publishToast } = useToaster();

    const form = useForm({ defaultValues: FORM_DEFAULT_VALUES, mode: 'onTouched' });
    const formErrors = form.formState.errors;

    // The toggle lives on the channel, not on the serializer config, so sync it even when no config exists yet.
    useEffect(() => {
        form.resetField('useOutputMapping', { defaultValue: supportsOutputMapper && !!useOutputMapping });
    }, [useOutputMapping, supportsOutputMapper]);

    useEffect(() => {
        if (!config) return;

        form.reset({
            useOutputMapping: supportsOutputMapper && !!useOutputMapping,
            enabled: config?.enabled,
            serializerModule: config?.serializerModule || '',

            headerFields: config?.headerFields || [],
            lineItemFields: config?.lineItemFields || [],
            exportExternalDocumentLink: config?.exportExternalDocumentLink || false,
            exportReceivedBy: config?.exportReceivedBy || false,
            exportWorkistUrl: config?.exportWorkistUrl || false,
            useShortExternalFileName: config?.useShortExternalFileName || false,
        });
    }, [config, useOutputMapping]);

    const outputMapperActive = supportsOutputMapper && form.watch('useOutputMapping');

    const outputMapperConfigMissing = useOutputMapperConfigMissing({
        outputMapperKey,
        ownerId: channelId,
        skip: !supportsOutputMapper || !outputMapperKey,
    });

    const handleSubmit = async ({ useOutputMapping: useOutputMappingValue, ...formData }) => {
        try {
            if (!config) await onActivate();
            await onSubmit(formData, supportsOutputMapper ? { useOutputMapping: useOutputMappingValue } : undefined);
            publishToast({ description: t('configPage.xmlSerializer.updateSuccess'), status: 'success' });
        } catch (e) {
            console.error(e);
            publishToast({ description: t('configPage.xmlSerializer.updateError'), status: 'error' });
        }
    };

    return (
        <SettingsFormCard onSubmit={form.handleSubmit(handleSubmit)} title={t('configPage.xmlSerializer.title')}>
            {supportsOutputMapper && (
                <Controller
                    name="useOutputMapping"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('configPage.xmlSerializer.form.useOutputMapping')}
                            error={formErrors.useOutputMapping}
                        >
                            <BooleanField {...field} value={field.value ? 'true' : ''} />
                        </FormField>
                    )}
                />
            )}

            {outputMapperActive && (
                <p className="text-sm text-content-medium">
                    {t('configPage.xmlSerializer.form.useOutputMappingActiveHint')}
                </p>
            )}

            {outputMapperActive && outputMapperConfigMissing && (
                <Alert severity="warning">
                    {t('configPage.xmlSerializer.form.useOutputMappingMissingConfigWarning')}
                </Alert>
            )}

            {!outputMapperActive && (
                <Controller
                    name="enabled"
                    control={form.control}
                    render={({ field }) => (
                        <FormField label={t('configPage.xmlSerializer.form.enabled')} error={formErrors.enabled}>
                            <BooleanField {...field} value={field.value ? 'true' : ''} />
                        </FormField>
                    )}
                />
            )}

            {!outputMapperActive && (
                <Controller
                    name="serializerModule"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('configPage.xmlSerializer.form.serializerModule')}
                            error={formErrors.serializerModule}
                        >
                            <StringField {...field} />
                        </FormField>
                    )}
                />
            )}

            <Controller
                name="headerFields"
                control={form.control}
                render={({ field }) => (
                    <FormField label={t('configPage.xmlSerializer.form.headerFields')} error={formErrors.headerFields}>
                        <TagsField {...field} onValueChange={field.onChange} />
                    </FormField>
                )}
            />

            <Controller
                name="lineItemFields"
                control={form.control}
                render={({ field }) => (
                    <FormField
                        label={t('configPage.xmlSerializer.form.lineItemFields')}
                        error={formErrors.lineItemFields}
                    >
                        <TagsField {...field} onValueChange={field.onChange} />
                    </FormField>
                )}
            />

            <Controller
                name="exportExternalDocumentLink"
                control={form.control}
                render={({ field }) => (
                    <FormField
                        label={t('configPage.xmlSerializer.form.exportExternalDocumentLink')}
                        error={formErrors.exportExternalDocumentLink}
                    >
                        <BooleanField {...field} value={field.value ? 'true' : ''} />
                    </FormField>
                )}
            />

            <Controller
                name="exportReceivedBy"
                control={form.control}
                render={({ field }) => (
                    <FormField
                        label={t('configPage.xmlSerializer.form.exportReceivedBy')}
                        error={formErrors.exportReceivedBy}
                    >
                        <BooleanField {...field} value={field.value ? 'true' : ''} />
                    </FormField>
                )}
            />

            <Controller
                name="exportWorkistUrl"
                control={form.control}
                render={({ field }) => (
                    <FormField
                        label={t('configPage.xmlSerializer.form.exportWorkistUrl')}
                        error={formErrors.exportWorkistUrl}
                    >
                        <BooleanField {...field} value={field.value ? 'true' : ''} />
                    </FormField>
                )}
            />

            <Controller
                name="useShortExternalFileName"
                control={form.control}
                render={({ field }) => (
                    <FormField
                        label={t('configPage.xmlSerializer.form.useShortExternalFileName')}
                        error={formErrors.useShortExternalFileName}
                    >
                        <BooleanField {...field} value={field.value ? 'true' : ''} />
                    </FormField>
                )}
            />

            <SaveStatus
                isDirty={form.formState.isDirty}
                isSubmitting={form.formState.isSubmitting}
                errorCount={countFieldErrors(form.formState.errors)}
            />
        </SettingsFormCard>
    );
};
export default XmlSerializerConfig;
